import React, { useMemo, useRef, useState } from 'react'

import { goodfellasApi } from '../../../apis'
import { useCompetitionStore } from '../../../functions/store'
import {
  AgeCategories,
  ChallengeType,
  ChallengeTypeLabels,
  CompetitionMatch,
  ConfirmationStatus,
  Decision,
  DecisionLabels,
  MatchFormInput,
  MatchMaster,
  MatchResult,
  Organizer,
  OrganizerLabels,
  ResultLabels,
  SubmissionTypeLabels,
  WeightCategories,
} from '../../../types/competition'
import MasterSelect from './MasterSelect'
import OpponentSearch from './OpponentSearch'

const CONFIRMATION_LABELS: Record<string, string> = {
  pending: 'Aguardando oponente',
  confirmed: 'Confirmada',
  disputed: 'Contestada',
  arbitration: 'Em arbitragem',
}

/** Resultado sempre gravado do ponto de vista do dono da luta. */
const RESULT_FROM_FIGHTER: Record<string, string> = {
  won: 'venceu',
  lost: 'perdeu',
  draw: 'empatou',
}

/**
 * Contexto do painel administrativo (#1292). Quando ausente, o modal é o do
 * próprio lutador e a cópia em primeira pessoa ("Você", "sua luta") está
 * correta. Quando presente, o admin está editando a luta de outra pessoa: a
 * tela passa a nomear os dois lados e pode arbitrar o resultado.
 */
export interface MatchAdminContext {
  /** Nome do dono da luta, para nomear o lado "de casa" no lugar de "Você". */
  fighterName?: string
  /** Libera o painel de arbitragem quando a luta ainda não foi confirmada. */
  canArbitrate?: boolean
}

interface AddCompetitionProps {
  onClose: () => void
  onSuccess?: () => void
  mode?: 'create' | 'edit'
  initialMatch?: CompetitionMatch
  // When set (admin flow), the new match is created for this fighter instead of
  // the authenticated user. Ignored on edit (the backend authorizes by matchId).
  profileUserUuid?: string
  adminContext?: MatchAdminContext
}

export default function AddCompetition({
  onClose,
  onSuccess,
  mode = 'create',
  initialMatch,
  profileUserUuid,
  adminContext,
}: AddCompetitionProps) {
  const { competitionActions } = useCompetitionStore()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{
    opponentName?: string
    fighterName?: string
  }>({})
  const opponentInputRef = useRef<HTMLInputElement>(null)

  const initialFormData = useMemo<MatchFormInput>(() => {
    if (mode !== 'edit' || !initialMatch) {
      return {
        eventName: '',
        eventDate: new Date().toISOString().split('T')[0],
        organizer: Organizer.OTHER,
        opponentName: '',
        opponentAcademy: '',
        result: MatchResult.WON,
        decision: Decision.POINTS,
        submission: false,
      }
    }

    const eventDate =
      typeof initialMatch.eventDate === 'string'
        ? initialMatch.eventDate.split('T')[0]
        : new Date(initialMatch.eventDate).toISOString().split('T')[0]

    return {
      eventName: initialMatch.eventName || '',
      eventDate,
      organizer: (initialMatch.organizer as Organizer) || Organizer.OTHER,
      challengeType: initialMatch.challengeType,
      opponentName: initialMatch.opponent?.name || '',
      opponentAcademy: initialMatch.opponent?.academy || '',
      fighterMaster: initialMatch.fighterMaster ?? null,
      opponentMaster: initialMatch.opponent?.master ?? null,
      result: initialMatch.result as MatchResult,
      decision: initialMatch.decision as Decision,
      submission: Boolean(initialMatch.submission),
      submissionType: initialMatch.submissionType || undefined,
      category: {
        weight: initialMatch.category?.weight,
        age: initialMatch.category?.age,
        beltColor: (
          initialMatch.category as { belt?: { color: string; degree: number } }
        )?.belt?.color,
        beltDegree: (
          initialMatch.category as { belt?: { color: string; degree: number } }
        )?.belt?.degree,
      },
      points: initialMatch.statistics?.points,
      advantages: initialMatch.statistics?.advantages,
      penalties: initialMatch.statistics?.penalties,
      opponentPoints: initialMatch.statistics?.opponentPoints,
      opponentAdvantages: initialMatch.statistics?.opponentAdvantages,
      opponentPenalties: initialMatch.statistics?.opponentPenalties,
      timeSeconds: initialMatch.statistics?.timeSeconds,
      // Sem semear aqui, o payload manda `videos: []` e o PUT apaga a mídia da
      // luta — e o vídeo é justamente a prova anti-fraude do #1292.
      videos: initialMatch.videos || [],
      photos: initialMatch.photos || [],
    }
  }, [initialMatch, mode])

  const [formData, setFormData] = useState<MatchFormInput>(initialFormData)

  const [timeText, setTimeText] = useState('')
  const [timeError, setTimeError] = useState<string | null>(null)

  const [selectedOpponent, setSelectedOpponent] = useState<{
    identificator?: { type: string; uuid: string } | null
  } | null>(() => {
    if (mode === 'edit' && initialMatch?.opponent?.identificator) {
      return { identificator: initialMatch.opponent.identificator }
    }
    return null
  })

  // --- 1º competidor (admin) ------------------------------------------------
  // Trocar o dono da luta é a correção para a luta lançada no atleta errado.
  // Só faz sentido no painel administrativo: no fluxo do próprio atleta a luta
  // é dele por definição.
  const canReassignFighter = Boolean(adminContext && mode === 'edit')
  const [fighterName, setFighterName] = useState(
    () => adminContext?.fighterName?.trim() || '',
  )
  const [selectedFighter, setSelectedFighter] = useState<{
    identificator?: { type: string; uuid: string } | null
  } | null>(() =>
    initialMatch?.fighter ? { identificator: initialMatch.fighter } : null,
  )
  const initialFighterUuid = initialMatch?.fighter?.uuid
  const nextFighterUuid = selectedFighter?.identificator?.uuid
  // No fluxo self-service o 1º competidor não tem campo de busca, mas o perfil
  // é conhecido pela prop — e o mestre dele cabe do mesmo jeito.
  const fighterUuid = canReassignFighter
    ? nextFighterUuid
    : profileUserUuid ?? initialFighterUuid
  // A academia que veio junto do perfil selecionado já aparece no selo do
  // `OpponentSearch`: perguntá-la de novo é pedir o que a busca acabou de
  // informar. O campo só sobra para o oponente digitado à mão — ou para o
  // perfil sem academia cadastrada —, onde nada mais pode fornecê-la.
  // Espelha `_pickedAcademy` do formulário do Sweep.
  const pickedAcademy =
    selectedOpponent?.identificator?.uuid && formData.opponentAcademy?.trim()
      ? formData.opponentAcademy.trim()
      : null

  const setMaster = (
    field: 'fighterMaster' | 'opponentMaster',
    master: MatchMaster | null,
  ) => setFormData((prev) => ({ ...prev, [field]: master }))
  const fighterReassigned =
    canReassignFighter &&
    Boolean(nextFighterUuid) &&
    nextFighterUuid !== initialFighterUuid

  // Luta nascida da conclusão de um desafio existe em PAR, um documento por
  // lutador. `source: 'system'` é a marca de quem a criou.
  const isChallengeBorn = initialMatch?.confirmation?.source === 'system'

  // --- Arbitragem (#1292) ---------------------------------------------------
  // O admin só decide quando a luta ainda não fechou. Luta legada (sem
  // `confirmation`) e luta já confirmada não abrem o painel.
  const confirmation = initialMatch?.confirmation
  const confirmationStatus = confirmation?.status
  const canArbitrate = Boolean(
    adminContext?.canArbitrate &&
      mode === 'edit' &&
      confirmationStatus &&
      confirmationStatus !== ConfirmationStatus.CONFIRMED,
  )
  // Contestada ou em impasse é exatamente o caso que trouxe o admin até aqui,
  // então o fecho já vem ligado. Pendente ele ainda está só esperando resposta.
  const [closeDispute, setCloseDispute] = useState(
    confirmationStatus === ConfirmationStatus.DISPUTED ||
      confirmationStatus === ConfirmationStatus.ARBITRATION,
  )

  const fighterLabel = adminContext?.fighterName?.trim() || 'Lutador'
  const opponentLabel =
    initialMatch?.opponent?.name?.trim() ||
    formData.opponentName?.trim() ||
    'Oponente'

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target
    const checked = (e.target as HTMLInputElement).checked
    const nextValue =
      type === 'checkbox'
        ? checked
        : type === 'number'
        ? value === ''
          ? undefined
          : Number(value)
        : value

    setFormData((prev) => {
      const next = { ...prev, [name]: nextValue }
      if (name === 'decision') {
        next.submission = nextValue === Decision.SUBMISSION
      }
      return next
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (timeError) return
    setFieldErrors({})
    setLoading(true)
    setError(null)

    try {
      const opponentName = (formData.opponentName || '').trim()

      // Um lutador solto não serve: a luta é indexada pelo perfil e pelo
      // telefone dele, então o 1º competidor tem de ser um perfil de verdade.
      if (canReassignFighter && fighterName.trim() && !nextFighterUuid) {
        setFieldErrors({
          fighterName: 'Selecione um atleta da busca, com perfil vinculado',
        })
        setError('Preencha os campos obrigatórios.')
        setLoading(false)
        return
      }

      const hasCategory =
        formData.category?.weight ||
        formData.category?.age ||
        formData.category?.beltColor
      const stats = {
        points: formData.points,
        advantages: formData.advantages,
        penalties: formData.penalties,
        timeSeconds: formData.timeSeconds,
        opponentPoints: formData.opponentPoints,
        opponentAdvantages: formData.opponentAdvantages,
        opponentPenalties: formData.opponentPenalties,
      }
      const hasStats = Object.values(stats).some(
        (v) => typeof v === 'number' && Number.isFinite(v),
      )
      const payload = {
        eventName: (formData.eventName || '').trim(),
        eventDate: formData.eventDate,
        organizer: formData.organizer,
        // A modalidade só existe na luta do Goodfellas — o backend descarta o
        // campo para as outras organizações de qualquer jeito.
        ...(formData.organizer === Organizer.GOODFELLAS && {
          challengeType: formData.challengeType || ChallengeType.ONE_ON_ONE,
        }),
        ...(hasCategory && {
          category: {
            weight: formData.category?.weight,
            age: formData.category?.age,
            belt: formData.category?.beltColor
              ? {
                  color: formData.category.beltColor,
                  degree: formData.category.beltDegree ?? 0,
                }
              : undefined,
          },
        }),
        ...(opponentName && {
          opponent: {
            name: opponentName,
            academy: (formData.opponentAcademy || '').trim() || undefined,
            identificator: selectedOpponent?.identificator ?? null,
            master: formData.opponentMaster ?? null,
          },
        }),
        fighterMaster: formData.fighterMaster ?? null,
        result: formData.result,
        decision: formData.decision,
        submission: formData.submission,
        submissionType: formData.submission
          ? formData.submissionType
          : undefined,
        videos: formData.videos || [],
        photos: formData.photos || [],
        ...(hasStats && {
          statistics: {
            points: formData.points,
            advantages: formData.advantages,
            penalties: formData.penalties,
            timeSeconds: formData.timeSeconds,
            opponentPoints: formData.opponentPoints,
            opponentAdvantages: formData.opponentAdvantages,
            opponentPenalties: formData.opponentPenalties,
          },
        }),
        // Admin-only: target another fighter on create (ignored on edit).
        ...(mode === 'create' && profileUserUuid ? { profileUserUuid } : {}),
        // Na edição, a mesma chave reatribui o dono da luta. Só vai quando
        // muda de verdade, para todo PUT que já existia seguir idêntico.
        ...(fighterReassigned ? { profileUserUuid: nextFighterUuid } : {}),
        // Arbitragem (#1292): fecha a disputa com o resultado que o admin
        // escolheu. Só admin pode mexer nisso — o backend ignora o campo para
        // qualquer outro solicitante.
        ...(canArbitrate && closeDispute
          ? { confirmation: { status: ConfirmationStatus.CONFIRMED } }
          : {}),
      }

      const response =
        mode === 'edit' && initialMatch
          ? await goodfellasApi.put(
              `/competition/matches/${initialMatch.identificator.uuid}`,
              payload,
              { validateStatus: () => true },
            )
          : await goodfellasApi.post(`/competition/matches`, payload, {
              validateStatus: () => true,
            })

      if (
        response.status >= 200 &&
        response.status < 300 &&
        response.data.success
      ) {
        if (mode === 'edit' && initialMatch) {
          competitionActions.updateMatch(
            initialMatch.identificator.uuid,
            response.data.data,
          )
        } else {
          competitionActions.addMatch(response.data.data)
        }
        onSuccess?.()
        onClose()
      } else {
        const apiError =
          response.data?.error ||
          response.data?.message ||
          'Erro ao salvar luta. Tente novamente.'
        // Best-effort: map common backend validation to field error
        const details = String(response.data?.details || '')
        if (details.includes('opponent') && details.includes('name')) {
          setFieldErrors({ opponentName: 'Informe o nome do oponente' })
          requestAnimationFrame(() => opponentInputRef.current?.focus())
        }
        setError(apiError)
      }
    } catch (err) {
      console.error('Error creating match:', err)
      setError('Erro ao salvar luta. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const inputClass =
    'w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all duration-200 text-gray-900'
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-2'
  const sectionClass =
    'bg-white rounded-2xl p-6 shadow-sm border border-gray-100'
  const sectionNumberClass =
    'w-9 h-9 bg-red-600 text-white rounded-lg flex items-center justify-center text-base font-extrabold flex-shrink-0'

  const parseMmssToSeconds = (value: string): number | null => {
    const v = value.trim()
    if (!v) return null
    const m = v.match(/^(\d{1,3}):([0-5]\d)$/)
    if (!m) return NaN
    const minutes = Number(m[1])
    const seconds = Number(m[2])
    return minutes * 60 + seconds
  }

  const formatSecondsToMmss = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000]">
      <div className="h-full w-full overflow-y-auto flex items-start justify-center px-4 py-6 sm:py-16">
        <div className="bg-gray-50 rounded-3xl shadow-2xl max-w-4xl w-full overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-500 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    {mode === 'edit' ? 'Editar Luta' : 'Registrar Nova Luta'}
                  </h2>
                  {adminContext ? (
                    // Painel admin: a luta é de outra pessoa, então a tela diz
                    // de quem e contra quem, em vez de "sua luta" (#1292).
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-white/90">
                      <span className="font-semibold">{fighterLabel}</span>
                      <span className="text-white/60">vs</span>
                      <span className="font-semibold">{opponentLabel}</span>
                      {initialMatch?.opponent?.identificator?.uuid ? (
                        <span className="rounded-md bg-white/20 px-2 py-0.5 text-xs font-semibold">
                          oponente vinculado
                        </span>
                      ) : (
                        <span className="rounded-md bg-yellow-300/25 px-2 py-0.5 text-xs font-semibold text-yellow-50">
                          sem perfil vinculado
                        </span>
                      )}
                      {confirmationStatus ? (
                        <span className="rounded-md bg-black/25 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide">
                          {CONFIRMATION_LABELS[confirmationStatus] ||
                            confirmationStatus}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <p className="text-white/80 text-sm">
                      {mode === 'edit'
                        ? 'Atualize as informações da sua luta'
                        : 'Adicione uma competição ao seu histórico'}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </div>
            )}

            {canArbitrate && (
              /* Arbitragem (#1292): as duas versões lado a lado e a decisão da
                 equipe. Os botões escrevem em `formData.result`, o mesmo campo
                 do select da seção 4 — uma fonte de verdade só. */
              <section className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
                <h3 className="mb-1 flex items-center gap-3 text-lg font-bold text-gray-900">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-amber-500 text-base font-extrabold text-white">
                    !
                  </span>
                  Arbitragem da equipe
                </h3>
                <p className="mb-5 text-sm text-gray-600">
                  As duas partes não chegaram a um acordo. Enquanto a luta não
                  for confirmada, ela não conta nas estatísticas nem no ranking.
                </p>

                <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Versão de {fighterLabel}
                    </span>
                    <p className="text-sm text-gray-900">
                      Registrou que{' '}
                      <strong>
                        {RESULT_FROM_FIGHTER[initialMatch?.result || ''] ||
                          initialMatch?.result}
                      </strong>
                    </p>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Versão de {opponentLabel}
                    </span>
                    {confirmation?.dispute ? (
                      <>
                        <p className="text-sm text-gray-900">
                          Alega que{' '}
                          <strong>
                            {/* `dispute.result` é gravado do ponto de vista do
                                oponente; aqui invertemos para a mesma régua do
                                lado esquerdo. */}
                            {confirmation.dispute.result === MatchResult.WON
                              ? 'venceu'
                              : confirmation.dispute.result === MatchResult.LOST
                              ? 'perdeu'
                              : 'empatou'}
                          </strong>
                        </p>
                        {confirmation.dispute.reason ? (
                          <p className="mt-2 text-sm text-gray-600">
                            “{confirmation.dispute.reason}”
                          </p>
                        ) : (
                          <p className="mt-2 text-sm text-gray-400">
                            Sem motivo informado
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-400">
                        Ainda não respondeu.
                      </p>
                    )}
                  </div>
                </div>

                <span className={labelClass}>Quem venceu?</span>
                <div className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {[
                    { value: MatchResult.WON, label: `${fighterLabel} venceu` },
                    {
                      value: MatchResult.LOST,
                      label: `${opponentLabel} venceu`,
                    },
                    { value: MatchResult.DRAW, label: 'Empate' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          result: option.value,
                        }))
                      }
                      aria-pressed={formData.result === option.value}
                      className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                        formData.result === option.value
                          ? 'border-red-600 bg-red-600 text-white'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-red-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <label className="flex cursor-pointer items-start gap-3 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={closeDispute}
                    onChange={(e) => setCloseDispute(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-red-600"
                  />
                  <span>
                    <strong>
                      Fechar a disputa e confirmar este resultado.
                    </strong>{' '}
                    A luta volta a contar nas estatísticas e sai da lista de
                    disputas. Desmarque para só corrigir dados sem decidir.
                  </span>
                </label>
              </section>
            )}

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Event Info Section */}
                <div className={sectionClass}>
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className={sectionNumberClass}>1</span>
                    Informações do Evento
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className={labelClass}>Nome do Evento *</label>
                      <input
                        type="text"
                        name="eventName"
                        value={formData.eventName}
                        onChange={handleChange}
                        required
                        placeholder="Ex: IBJJF Brasileiro 2024"
                        className={inputClass}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>Data *</label>
                        <input
                          type="date"
                          name="eventDate"
                          value={formData.eventDate}
                          onChange={handleChange}
                          required
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Organizador</label>
                        <select
                          name="organizer"
                          value={formData.organizer}
                          onChange={handleChange}
                          className={inputClass}
                        >
                          {Object.entries(OrganizerLabels).map(
                            ([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                      {formData.organizer === Organizer.GOODFELLAS && (
                        <div>
                          <label className={labelClass}>Tipo de desafio</label>
                          <select
                            name="challengeType"
                            value={
                              formData.challengeType || ChallengeType.ONE_ON_ONE
                            }
                            onChange={handleChange}
                            className={inputClass}
                          >
                            {Object.entries(ChallengeTypeLabels).map(
                              ([value, label]) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ),
                            )}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Category Section */}
                <div className={sectionClass}>
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className={sectionNumberClass}>2</span>
                    Categoria
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Peso</label>
                      <select
                        value={formData.category?.weight || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            category: {
                              ...prev.category,
                              weight: e.target.value,
                            },
                          }))
                        }
                        className={inputClass}
                      >
                        <option value="">Selecione</option>
                        {WeightCategories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Faixa Etária</label>
                      <select
                        value={formData.category?.age || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            category: { ...prev.category, age: e.target.value },
                          }))
                        }
                        className={inputClass}
                      >
                        <option value="">Selecione</option>
                        {AgeCategories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Opponent Section */}
                <div className={sectionClass}>
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className={sectionNumberClass}>3</span>
                    {canReassignFighter ? 'Competidores' : 'Oponente'}
                  </h3>

                  <div className="space-y-4">
                    {canReassignFighter && (
                      <div>
                        <label className={labelClass}>1º competidor</label>
                        <OpponentSearch
                          value={fighterName}
                          onChange={(value) => {
                            setFighterName(value)
                            // Digitar desvincula: sem perfil não há para quem
                            // reatribuir, e o submit cobra a seleção.
                            setSelectedFighter(null)
                            setMaster('fighterMaster', null)
                          }}
                          onSelect={(fighter) => {
                            setSelectedFighter(fighter)
                            // Outro competidor, outra academia: o mestre
                            // anterior não dá aula para este.
                            setMaster('fighterMaster', null)
                          }}
                          placeholder="Buscar atleta por nome ou telefone"
                          hasError={Boolean(fieldErrors.fighterName)}
                          initialSelected={
                            initialMatch?.fighter
                              ? {
                                  name:
                                    adminContext?.fighterName?.trim() ||
                                    'Lutador',
                                  identificator: initialMatch.fighter,
                                }
                              : null
                          }
                        />
                        {fieldErrors.fighterName && (
                          <p className="mt-2 text-sm text-red-600 font-medium">
                            {fieldErrors.fighterName}
                          </p>
                        )}
                        {fighterReassigned && (
                          <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            <p>
                              A luta passa para o novo atleta com o mesmo
                              resultado: <strong>trocar o competidor não
                              inverte vitória e derrota</strong>. O oponente
                              volta a ter de confirmar, e as estatísticas e o
                              ranking dos dois mudam na hora.
                            </p>
                            {isChallengeBorn && (
                              <p className="mt-2">
                                Esta luta veio da conclusão de um desafio, que
                                grava <strong>uma luta para cada lutador</strong>.
                                A luta do outro atleta não muda aqui — edite-a
                                separadamente.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Fora do painel admin não há campo de 1º competidor:
                        sem o perfil dele em mãos o select ficaria morto para
                        sempre, então nem aparece. */}
                    {(canReassignFighter || fighterUuid) && (
                      <MasterSelect
                        label={
                          canReassignFighter
                            ? 'Mestre do 1º competidor'
                            : 'Seu mestre'
                        }
                        competitorUuid={fighterUuid}
                        value={formData.fighterMaster}
                        onChange={(master) =>
                          setMaster('fighterMaster', master)
                        }
                      />
                    )}
                    <div>
                      <label className={labelClass}>
                        {canReassignFighter ? '2º competidor' : 'Nome do Oponente'}
                      </label>
                      <OpponentSearch
                        value={formData.opponentName}
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            opponentName: value,
                          }))
                        }
                        onSelect={(opponent) => {
                          setSelectedOpponent(opponent)
                          // Sem isto a academia do oponente ANTERIOR ficava no
                          // formulário e era regravada na troca.
                          setFormData((prev) => ({
                            ...prev,
                            opponentAcademy: opponent?.academy || '',
                            // Outro oponente, outra academia: o mestre anterior
                            // não dá aula para este.
                            opponentMaster: null,
                          }))
                        }}
                        placeholder="Buscar ou digitar nome"
                        hasError={Boolean(fieldErrors.opponentName)}
                        ref={opponentInputRef}
                        initialSelected={
                          mode === 'edit' &&
                          initialMatch?.opponent?.identificator
                            ? {
                                name: initialMatch.opponent.name,
                                academy: initialMatch.opponent.academy,
                                identificator:
                                  initialMatch.opponent.identificator,
                              }
                            : null
                        }
                      />
                      {fieldErrors.opponentName && (
                        <p className="mt-2 text-sm text-red-600 font-medium">
                          {fieldErrors.opponentName}
                        </p>
                      )}
                    </div>
                    <MasterSelect
                      label={
                        canReassignFighter
                          ? 'Mestre do 2º competidor'
                          : 'Mestre do Oponente'
                      }
                      competitorUuid={selectedOpponent?.identificator?.uuid}
                      value={formData.opponentMaster}
                      onChange={(master) => setMaster('opponentMaster', master)}
                    />
                    {!pickedAcademy && (
                      <div>
                        <label className={labelClass}>
                          Academia do Oponente
                        </label>
                        <input
                          type="text"
                          name="opponentAcademy"
                          value={formData.opponentAcademy || ''}
                          onChange={handleChange}
                          placeholder="Ex: GFT São Paulo"
                          className={inputClass}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Result Section */}
                <div className={sectionClass}>
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className={sectionNumberClass}>4</span>
                    Resultado da Luta
                  </h3>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>Resultado *</label>
                        <select
                          name="result"
                          value={formData.result}
                          onChange={handleChange}
                          required
                          className={inputClass}
                        >
                          {Object.entries(ResultLabels).map(
                            ([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Decisão *</label>
                        <select
                          name="decision"
                          value={formData.decision}
                          onChange={handleChange}
                          required
                          className={inputClass}
                        >
                          {Object.entries(DecisionLabels).map(
                            ([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                    </div>

                    {formData.decision === Decision.SUBMISSION && (
                      <div>
                        <label className={labelClass}>
                          Tipo de Finalização
                        </label>
                        <select
                          name="submissionType"
                          value={formData.submissionType || ''}
                          onChange={handleChange}
                          className={inputClass}
                        >
                          <option value="">Selecione</option>
                          {Object.entries(SubmissionTypeLabels).map(
                            ([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics Section - Full Width */}
            <div className={sectionClass}>
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-gray-200 text-gray-600 rounded-lg flex items-center justify-center text-sm font-bold">
                  ⚡
                </span>
                Estatísticas (opcional)
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-bold text-gray-900">
                      {adminContext ? fighterLabel : 'Você'}
                    </span>
                    <span className="text-xs font-semibold text-gray-500">
                      {adminContext ? 'Placar do lutador' : 'Seu placar'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className={labelClass}>Pontos</label>
                      <input
                        type="number"
                        name="points"
                        value={formData.points ?? ''}
                        onChange={handleChange}
                        min="0"
                        placeholder="0"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Vantagens</label>
                      <input
                        type="number"
                        name="advantages"
                        value={formData.advantages ?? ''}
                        onChange={handleChange}
                        min="0"
                        placeholder="0"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Punições</label>
                      <input
                        type="number"
                        name="penalties"
                        value={formData.penalties ?? ''}
                        onChange={handleChange}
                        min="0"
                        placeholder="0"
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-bold text-gray-900">
                      {adminContext ? opponentLabel : 'Adversário'}
                    </span>
                    <span className="text-xs font-semibold text-gray-500">
                      Placar do oponente
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className={labelClass}>Pontos</label>
                      <input
                        type="number"
                        name="opponentPoints"
                        value={formData.opponentPoints ?? ''}
                        onChange={handleChange}
                        min="0"
                        placeholder="0"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Vantagens</label>
                      <input
                        type="number"
                        name="opponentAdvantages"
                        value={formData.opponentAdvantages ?? ''}
                        onChange={handleChange}
                        min="0"
                        placeholder="0"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Punições</label>
                      <input
                        type="number"
                        name="opponentPenalties"
                        value={formData.opponentPenalties ?? ''}
                        onChange={handleChange}
                        min="0"
                        placeholder="0"
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <label className={labelClass}>Tempo (mm:ss)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={
                    timeText ||
                    (typeof formData.timeSeconds === 'number'
                      ? formatSecondsToMmss(formData.timeSeconds)
                      : '')
                  }
                  onChange={(e) => {
                    const v = e.target.value
                    setTimeText(v)
                    const seconds = parseMmssToSeconds(v)
                    if (seconds === null) {
                      setTimeError(null)
                      setFormData((prev) => ({
                        ...prev,
                        timeSeconds: undefined,
                      }))
                      return
                    }
                    if (Number.isNaN(seconds)) {
                      setTimeError('Use o formato mm:ss (ex: 05:00)')
                      return
                    }
                    setTimeError(null)
                    setFormData((prev) => ({ ...prev, timeSeconds: seconds }))
                  }}
                  onBlur={() => {
                    const seconds = parseMmssToSeconds(timeText)
                    if (typeof seconds === 'number' && !Number.isNaN(seconds)) {
                      setTimeText(formatSecondsToMmss(seconds))
                    }
                  }}
                  placeholder="05:00"
                  className={[
                    inputClass,
                    timeError ? 'border-red-500 focus:ring-red-500' : '',
                  ].join(' ')}
                />
                {timeError && (
                  <p className="mt-2 text-sm text-red-600 font-medium">
                    {timeError}
                  </p>
                )}
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-4 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-xl hover:from-red-700 hover:to-red-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium shadow-lg shadow-red-500/25"
              >
                {loading && (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                )}
                {loading
                  ? 'Salvando...'
                  : mode === 'edit'
                  ? 'Salvar alterações'
                  : 'Salvar Luta'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
